import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../Accounts/AuthContext';
import {
  Home,          // Home (student dashboard)
  Search,        // Diagnostic
  BookOpen,      // Bluebook upload + report
  GraduationCap, // College Explorer
  KeyRound,      // Join a class (code)
  Mail,          // Contact
  Settings,      // Settings page
  User,          // Account
  LogOut,        // Log out
  ChevronsLeft,  // collapse
  ChevronsRight, // expand
  ChevronDown,   // phone dropdown caret
  LayoutGrid,    // Teacher/Admin dashboard
} from 'lucide-react';
import BrandLockup from '../components/brand/BrandLockup';
import JoinClassModal from '../StudentDashboardV3/components/JoinClassModal';

// App chrome (Sandy 2026-07-24, made role-aware 2026-07-27): every signed-in
// user gets this left rail instead of the fixed top navbar, so the page keeps
// its vertical space. Modeled on the teacher rail (TeacherDashboardV3/
// components/DashboardShell): white canvas, neutral-200 hairline border, ink
// text, neutral-100 active wash. Only marketing routes get NavigationBar now.
//
// Teachers and admins see this rail on the routes OUTSIDE the V3 dashboard
// (account, contact, the legacy dashboards). On /teacher-dashboard/* the
// page renders its own rail, and App.js suppresses this one so there is never
// more than one nav on screen.
export const RAIL_WIDTH_EXPANDED = 224; // w-56
export const RAIL_WIDTH_COLLAPSED = 56; // w-14

const COLLAPSE_KEY = 'bb:studentRailCollapsed';

// Question surfaces: the rail drops to icons only so the passage/answers get
// the full width. This wins over the student's manual preference.
const isQuestionRoute = (pathname) =>
  pathname.startsWith('/session/') || pathname.startsWith('/lesson-preview/');

// Below the lg breakpoint a 224px rail eats most of the screen, so it stays
// icon-only there no matter what the student picked on a laptop.
const NARROW_QUERY = '(max-width: 1023px)';

// Phones (Sandy 2026-08-18): even the 56px icon rail is ~15% of a 375px
// screen, so below md the rail is replaced by a top bar with a dropdown -
// Buffy + the current tab's name + a caret; the menu holds every rail item.
const PHONE_QUERY = '(max-width: 767px)';

const readStoredCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false; // private mode / storage disabled
  }
};

// The rail's brand header (Sandy 2026-07-27, shared 2026-08-04): expanded
// keeps the ORIGINAL logo image with its original text colors; collapsed
// shows the sitting Buffy, sized up and nudged down so he straddles the
// header's bottom hairline and fills that corner — the border still runs
// behind him on both sides. The nudge is a transform, so it costs no layout
// and the nav rows below neither shift nor get overlapped (his feet land at
// ~61px, the first row starts at 68px). Exported so the teacher rail
// (TeacherDashboardV3/components/DashboardShell.jsx) renders the EXACT same
// header — one component, no drift.
// The new brand lockup (Sandy 2026-10-13: "the new logo in the top left" on
// the student, teacher and admin dashboards), replacing the old blue logo
// image and the sitting Buffy. Expanded: the full lockup at the marketing
// nav's size. Collapsed (56px rail): the pixel head alone, at 28px so it
// lands on whole device pixels at 1x and 2x. Both stay mounted and collapse
// only toggles which one shows, so nothing re-paints mid-transition.
// `to`: the student rail passes /students; "/" is the teacher page (2026-11-03).
export function RailBrandHeader({ collapsed, to = '/' }) {
  return (
    <Link
      to={to}
      title="Brain Buffs"
      className={
        'flex-none flex items-center h-[56px] border-b border-neutral-100 no-underline overflow-hidden ' +
        (collapsed ? 'justify-center px-[4px]' : 'px-[16px]')
      }
    >
      <span className={collapsed ? 'hidden' : 'flex'}><BrandLockup size={42} /></span>
      <span className={collapsed ? 'flex' : 'hidden'}><BrandLockup size={28} headOnly /></span>
    </Link>
  );
}

export default function StudentSideNav() {
  const auth = useAuth() || {};
  const { isLoggedIn, user, logout } = auth;
  // Admin view mode: logout() leaves view mode, so the row says so.
  const logOutLabel = auth.viewAs ? 'Exit view mode' : 'Log out';
  const location = useLocation();

  // Manual preference; the auto-collapse below overrides it on question routes.
  const [manualCollapsed, setManualCollapsed] = useState(readStoredCollapsed);
  // Join-a-class modal (Sandy 2026-08-12): opened from the rail item below.
  const [joinOpen, setJoinOpen] = useState(false);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(NARROW_QUERY).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    const onChange = (e) => setIsNarrow(e.matches);
    setIsNarrow(mq.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange); // older Safari
    return () => mq.removeListener(onChange);
  }, []);

  const [isPhone, setIsPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(PHONE_QUERY);
    const onChange = (e) => setIsPhone(e.matches);
    setIsPhone(mq.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange); // older Safari
    return () => mq.removeListener(onChange);
  }, []);

  // Phone dropdown open state; navigating anywhere closes it.
  const [phoneOpen, setPhoneOpen] = useState(false);
  useEffect(() => { setPhoneOpen(false); }, [location.pathname]);

  const forcedCollapsed = isQuestionRoute(location.pathname) || isNarrow;
  const collapsed = forcedCollapsed || manualCollapsed;

  const toggleCollapsed = () => {
    setManualCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        /* private mode */
      }
      return next;
    });
  };

  const handleSignOut = async () => {
    try {
      await logout?.();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const path = location.pathname;
  const role = user?.role;
  const isStaff = role === 'Teacher' || role === 'Admin';

  // Home is the V3 dashboard (Sandy 2026-07-27): the old /student-dashboard is
  // no longer linked from the rail, and the temporary "NEW" entry is gone.
  const STUDENT_ITEMS = [
    {
      key: 'home',
      label: 'Home',
      Icon: Home,
      to: '/student-dashboard',
      active:
        path.startsWith('/student-dashboard') ||
        path.startsWith('/session/') ||
        path.startsWith('/domain/') ||
        path.startsWith('/lesson-preview/') ||
        path === '/subjects',
    },
    {
      key: 'diagnostic',
      label: 'Diagnostic',
      Icon: Search,
      to: '/diagnostic',
      active: path === '/diagnostic' || path.startsWith('/diagnostic/'),
    },
    {
      key: 'bluebook',
      // Two tabs since 2026-10-27: Bluebook test + PSAT/NMSQT report.
      label: 'Bluebook / PSAT',
      Icon: BookOpen,
      to: '/bluebook/upload',
      active: path.startsWith('/bluebook'),
    },
    {
      key: 'colleges',
      label: 'Colleges',
      Icon: GraduationCap,
      to: '/colleges',
      active: path === '/colleges' || path.startsWith('/colleges/'),
    },
    // Join a class (Sandy 2026-08-12): moved off the home page into the rail.
    // A button, not a route — it opens the centered code modal.
    {
      key: 'join',
      label: 'Join a class',
      Icon: KeyRound,
      onClick: () => setJoinOpen(true),
      active: false,
    },
    {
      key: 'contact',
      label: 'Contact',
      Icon: Mail,
      to: '/contact',
      active: path === '/contact',
    },
  ];

  // Staff see a short rail: the way back into the dashboard (which then takes
  // over with its own rail) plus the handful of app pages that live outside it.
  // Deliberately NOT the student links — Bluebook and the diagnostic are
  // student actions and read as broken in a teacher's chrome.
  const STAFF_ITEMS = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      Icon: LayoutGrid,
      to: role === 'Admin' ? '/admin-dashboard' : '/teacher-dashboard',
      active:
        path.startsWith('/teacher-dashboard') ||
        path.startsWith('/admin-dashboard'),
    },
    {
      key: 'contact',
      label: 'Contact',
      Icon: Mail,
      to: '/contact',
      active: path === '/contact',
    },
  ];

  const NAV_ITEMS = isStaff ? STAFF_ITEMS : STUDENT_ITEMS;

  // One row shape for links, the account chip and the log-out button, so the
  // rail reads as a single column at both widths.
  const rowClass = (active, tone = 'default') => {
    const base =
      'flex items-center h-[40px] rounded-[8px] no-underline border-0 w-full cursor-pointer transition-colors ' +
      (collapsed ? 'justify-center px-0 gap-0 ' : 'justify-start px-[12px] gap-[12px] ');
    if (active) return base + 'bg-neutral-100 text-ink';
    if (tone === 'danger') return base + 'bg-transparent text-red-600 hover:bg-red-50';
    return base + 'bg-transparent text-neutral-500 hover:bg-neutral-50 hover:text-ink';
  };

  const displayName = user?.Name || user?.email || null;

  // ---- Phone chrome: top bar + dropdown instead of the rail ----------------
  if (isPhone) {
    const current = NAV_ITEMS.find((i) => i.active)
      || (path === '/manageAccount' ? { label: 'Settings' } : null);
    const menuRow = (active, tone = 'default') => {
      const base =
        'flex items-center gap-3 h-[44px] rounded-[8px] px-3 w-full no-underline border-0 cursor-pointer transition-colors text-[15px] font-semibold ';
      if (active) return base + 'bg-neutral-100 text-ink';
      if (tone === 'danger') return base + 'bg-transparent text-red-600 hover:bg-red-50';
      return base + 'bg-transparent text-neutral-600 hover:bg-neutral-50 hover:text-ink';
    };
    return (
      <header className="sticky top-0 z-40 w-full bg-white border-b border-neutral-200 font-sans text-left antialiased">
        <div className="relative flex items-center h-[52px] px-3">
          {/* The pixel head, same as the collapsed rail header. Students go to
              the student landing; "/" is the teacher page since 2026-11-03. */}
          <Link to="/students" title="Brain Buffs" className="flex-none flex items-center">
            <BrandLockup size={28} headOnly />
          </Link>
          {/* Current tab + caret = the whole dropdown control. */}
          <button
            type="button"
            onClick={() => setPhoneOpen((o) => !o)}
            aria-expanded={phoneOpen}
            className="ml-2 flex items-center gap-1.5 bg-transparent border-0 cursor-pointer text-ink text-[16px] font-semibold px-2 h-[40px] rounded-[8px] hover:bg-neutral-50"
          >
            {current?.label || 'Menu'}
            <ChevronDown
              size={18}
              strokeWidth={2.5}
              className={`transition-transform ${phoneOpen ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
        {phoneOpen && (
          <>
            {/* click-away backdrop under the panel */}
            <div className="fixed inset-0 z-30" onClick={() => setPhoneOpen(false)} />
            {/* The panel hangs off a sticky 52px header, so without a cap it just
                runs past the bottom of the window and the last rows (Settings, the
                name, Log out) become unreachable: the page cannot scroll a sticky
                header's overflow into view. Measured at 700x420 the panel wanted
                466px and Log out fell off (Sandy 2026-08-23). Cap it to the space
                below the bar and let it scroll itself. */}
            <div className="absolute left-0 right-0 top-full z-40 bg-white border-b border-neutral-200 shadow-[0_24px_48px_-16px_rgba(20,20,20,0.25)] px-2 pt-1 pb-2 max-h-[calc(100vh-52px)] overflow-y-auto overscroll-contain">
              {NAV_ITEMS.map(({ key, label, Icon, to, active, onClick }) => (
                onClick ? (
                  <button key={key} type="button" onClick={() => { setPhoneOpen(false); onClick(); }} className={menuRow(active)}>
                    <Icon size={20} strokeWidth={2} className="flex-none" />
                    {label}
                  </button>
                ) : (
                  <Link key={key} to={to} className={menuRow(active)}>
                    <Icon size={20} strokeWidth={2} className="flex-none" />
                    {label}
                  </Link>
                )
              ))}
              <div className="h-px bg-neutral-200 my-1 mx-3" />
              {isLoggedIn && displayName ? (
                <>
                  <Link to="/manageAccount" className={menuRow(path === '/manageAccount')}>
                    <Settings size={20} strokeWidth={2} className="flex-none" />
                    Settings
                  </Link>
                  <div className="flex items-center gap-3 h-[40px] px-3 text-neutral-400 text-[14px] font-semibold">
                    <User size={20} strokeWidth={2} className="flex-none" />
                    <span className="min-w-0 truncate">{displayName}</span>
                  </div>
                  <button type="button" onClick={handleSignOut} className={menuRow(false, 'danger')}>
                    <LogOut size={20} strokeWidth={2} className="flex-none" />
                    {logOutLabel}
                  </button>
                </>
              ) : (
                <div className="flex items-center gap-3 h-[40px] px-3 text-neutral-400 text-[14px] font-semibold">
                  <User size={20} strokeWidth={2} className="flex-none" />
                  Preview
                </div>
              )}
            </div>
          </>
        )}
        {joinOpen && isLoggedIn && <JoinClassModal onClose={() => setJoinOpen(false)} />}
      </header>
    );
  }

  return (
    <aside
      className="flex-none self-start sticky top-0 h-screen bg-white border-r border-neutral-200 flex flex-col font-sans text-left antialiased"
      style={{ width: collapsed ? RAIL_WIDTH_COLLAPSED : RAIL_WIDTH_EXPANDED }}
    >
      <RailBrandHeader collapsed={collapsed} to="/students" />

      {/* Links (an item with onClick renders as a button — Join a class). */}
      <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-[4px] py-[12px] px-[8px]">
        {NAV_ITEMS.map(({ key, label, Icon, to, active, onClick }) => (
          onClick ? (
            <button key={key} type="button" onClick={onClick} title={label} className={rowClass(active)}>
              <Icon size={20} strokeWidth={2} className="flex-none" />
              {!collapsed && (
                <span className="text-[14px] font-semibold leading-none whitespace-nowrap">
                  {label}
                </span>
              )}
            </button>
          ) : (
            <Link key={key} to={to} title={label} className={rowClass(active)}>
              <Icon size={20} strokeWidth={2} className="flex-none" />
              {!collapsed && (
                <span className="text-[14px] font-semibold leading-none whitespace-nowrap">
                  {label}
                </span>
              )}
            </Link>
          )
        ))}
      </nav>

      {/* Manual collapse toggle. Hidden on question routes, where the rail is
          collapsed for the student and the toggle would be a dead control. */}
      {!forcedCollapsed && (
        <div className="flex-none px-[8px] pb-[8px]">
          <button
            type="button"
            onClick={toggleCollapsed}
            title={manualCollapsed ? 'Expand menu' : 'Collapse menu'}
            className={rowClass(false)}
          >
            {manualCollapsed ? (
              <ChevronsRight size={20} strokeWidth={2} className="flex-none" />
            ) : (
              <ChevronsLeft size={20} strokeWidth={2} className="flex-none" />
            )}
            {!collapsed && (
              <span className="text-[14px] font-semibold leading-none whitespace-nowrap">
                Collapse
              </span>
            )}
          </button>
        </div>
      )}

      {/* Account block (Sandy 2026-08-12): Settings opens the settings page,
          the name is just a label (not a link), Log out signs out. In the
          workbench audit preview there may be no signed-in user, so the
          fallback stays neutral and never touches user.* unguarded. */}
      <div className="flex-none border-t border-neutral-200 px-[8px] py-[8px] flex flex-col gap-[4px]">
        {isLoggedIn && displayName ? (
          <>
            <Link
              to="/manageAccount"
              title="Settings"
              className={rowClass(path === '/manageAccount')}
            >
              <Settings size={20} strokeWidth={2} className="flex-none" />
              {!collapsed && (
                <span className="text-[14px] font-semibold leading-none whitespace-nowrap">
                  Settings
                </span>
              )}
            </Link>
            <div
              title={displayName}
              className={
                'flex items-center h-[40px] rounded-[8px] w-full text-neutral-400 cursor-default ' +
                (collapsed ? 'justify-center px-0 gap-0' : 'justify-start px-[12px] gap-[12px]')
              }
            >
              <User size={20} strokeWidth={2} className="flex-none" />
              {!collapsed && (
                <span className="text-[14px] font-semibold leading-none min-w-0 truncate">
                  {displayName}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              title={logOutLabel}
              className={rowClass(false, 'danger')}
            >
              <LogOut size={20} strokeWidth={2} className="flex-none" />
              {!collapsed && (
                <span className="text-[14px] font-semibold leading-none whitespace-nowrap">
                  {logOutLabel}
                </span>
              )}
            </button>
          </>
        ) : (
          <div className={rowClass(false) + ' pointer-events-none'} title="Preview">
            <User size={20} strokeWidth={2} className="flex-none" />
            {!collapsed && (
              <span className="text-[14px] font-semibold leading-none whitespace-nowrap">
                Preview
              </span>
            )}
          </div>
        )}
      </div>

      {joinOpen && isLoggedIn && <JoinClassModal onClose={() => setJoinOpen(false)} />}
    </aside>
  );
}
